'use client';

import { type ReactNode, useEffect, useRef, useState } from 'react';

type Phase = 'pending' | 'play' | 'done';

// 사진 디코드가 이보다 늦으면 기다리지 않고 시작한다 — 느린 망에서 빈 화면이 길어지지 않게
const MAX_WAIT_MS = 2500;
// 타임라인의 마지막 애니메이션. 이게 끝나면 done — 케이크는 CSS 가 숨긴다
const LAST_ANIMATION = 'ty-hero-hat-move';

/**
 * 히어로 4.2초 인트로를 재생한다. 모션·케이크 숨김은 전부 hero.css 가 맡고, 여기서는
 * 사진 로딩을 기다렸다 [data-intro] 를 play 로, 마지막 애니메이션이 끝나면 done 으로 바꾼다.
 * done 은 되돌아가지 않으므로 스크롤로 재진입해도 다시 재생되지 않는다.
 */
export function HeroMotion({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>('pending');

  useEffect(function playWhenImagesReady() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setPhase('done');
      return;
    }
    let cancelled = false;
    // 케이크·파티햇 img 도 포함된다 — 첫 프레임인 케이크가 늦게 뜨면 연출이 깨진다
    const images = Array.from(ref.current?.querySelectorAll('img') ?? []);
    // 깨진 이미지도 decode 가 reject 되므로 삼키고 진행한다
    const decoded = Promise.all(
      images.map(img => img.decode().catch(() => {})),
    );
    const timeout = new Promise(resolve => setTimeout(resolve, MAX_WAIT_MS));
    Promise.race([decoded, timeout]).then(() => {
      if (!cancelled) setPhase('play');
    });
    return function cancelPlay() {
      cancelled = true;
    };
  }, []);

  return (
    <div
      ref={ref}
      data-intro={phase}
      // 모바일: 12칸 그리드(사진당 2칸, 1행은 한 칸 안쪽) / 데스크탑: Figma 1200×404 필드에 사진을 % 로 절대배치
      className="relative mx-auto grid w-full max-w-[361px] grid-cols-12 gap-x-[4.102px] gap-y-[4.141px] md:block md:aspect-[1200/404] md:max-w-[1200px]"
      onAnimationEnd={event => {
        if (event.animationName === LAST_ANIMATION) setPhase('done');
      }}
    >
      {children}
    </div>
  );
}
